import type { HourPrice } from '@greenbow/composables'
import type { SavingsResult, PriceStats, AnnualSavings } from '../types'

// Default assumes a 3-phase home charger (typical modern installation
// in Denmark). Users with a 1-phase plug or a public AC charger can
// adjust the value via the ChargeRateInput field.
export const DEFAULT_CHARGE_RATE_KW = 11

// Simple rule of thumb: 5 km per kWh = 20 kWh per 100 km.
// Slightly more conservative than the WLTP average but lines up with
// how Greenbow users mentally budget their charging.
export const DEFAULT_CONSUMPTION_KWH_PER_100KM = 20

export function calculateSavings(
  prices: readonly HourPrice[],
  kwhNeeded: number,
  chargeRateKw: number = DEFAULT_CHARGE_RATE_KW,
): SavingsResult {
  if (kwhNeeded <= 0 || prices.length === 0 || chargeRateKw <= 0) {
    return {
      smartCost: 0,
      averageCost: 0,
      savings: 0,
      optimalHours: [],
      kwhNeeded,
      chargeRateKw,
    }
  }

  // Tag the chronological position so we can restore it after sorting by price.
  const indexed = prices.map((p, order) => ({ ...p, order }))
  const sortedByPrice = [...indexed].sort((a, b) => a.price - b.price)
  const fullHoursCount = Math.floor(kwhNeeded / chargeRateKw)
  const partialKwh = kwhNeeded - fullHoursCount * chargeRateKw

  const fullHours = sortedByPrice.slice(0, fullHoursCount)
  const partialHour = partialKwh > 0 ? sortedByPrice[fullHoursCount] : undefined

  const smartCost =
    fullHours.reduce((sum, h) => sum + h.price * chargeRateKw, 0) +
    (partialHour ? partialHour.price * partialKwh : 0)

  const totalPrice = prices.reduce((sum, h) => sum + h.price, 0)
  const averagePrice = totalPrice / prices.length
  const averageCost = averagePrice * kwhNeeded

  const optimalHourSet = [...fullHours]
  if (partialHour) optimalHourSet.push(partialHour)
  // Sort by chronological order in the eligible window, not by hour number.
  // That way "22:00, 23:00, 00:00, 01:00..." stays in charging order even
  // when the window spans midnight.
  const optimalHours = optimalHourSet.sort((a, b) => a.order - b.order).map((h) => h.hour)

  return {
    smartCost,
    averageCost,
    savings: averageCost - smartCost,
    optimalHours,
    kwhNeeded,
    chargeRateKw,
  }
}

/**
 * Projects a per-session SavingsResult onto a full year based on how
 * many kilometres the user drives. The projection assumes today's
 * price pattern repeats throughout the year — documented as an
 * optimistic estimate in the disclaimer, not a guarantee.
 */
export function calculateAnnualSavings(
  perSession: SavingsResult,
  kmPerYear: number,
  consumptionKwhPer100km: number = DEFAULT_CONSUMPTION_KWH_PER_100KM,
): AnnualSavings {
  if (kmPerYear <= 0 || consumptionKwhPer100km <= 0 || perSession.kwhNeeded <= 0) {
    return {
      kmPerYear,
      kwhPerYear: 0,
      sessionsPerYear: 0,
      annualSmartCost: 0,
      annualAverageCost: 0,
      annualSavings: 0,
    }
  }

  const kwhPerYear = (kmPerYear * consumptionKwhPer100km) / 100
  const sessionsPerYear = kwhPerYear / perSession.kwhNeeded

  return {
    kmPerYear,
    kwhPerYear,
    sessionsPerYear,
    annualSmartCost: perSession.smartCost * sessionsPerYear,
    annualAverageCost: perSession.averageCost * sessionsPerYear,
    annualSavings: perSession.savings * sessionsPerYear,
  }
}

export function calculatePriceStats(prices: readonly HourPrice[], currentHour: number): PriceStats {
  if (prices.length === 0) {
    return { current: null, cheapest: null, expensive: null, average: null }
  }

  let cheapest = prices[0]!
  let expensive = prices[0]!
  let total = 0

  for (const p of prices) {
    if (p.price < cheapest.price) cheapest = p
    if (p.price > expensive.price) expensive = p
    total += p.price
  }

  const currentEntry = prices.find((p) => p.hour === currentHour)

  return {
    current: currentEntry ? currentEntry.price : null,
    cheapest: { hour: cheapest.hour, price: cheapest.price },
    expensive: { hour: expensive.hour, price: expensive.price },
    average: total / prices.length,
  }
}
